/*
 * bg-card-hover.js — Bombay Gothic product card hover cycling.
 *
 * Added 2026-05-18, task 0.8e.
 *
 * What this does:
 *   On mouseenter of a `.card-wrapper[data-hover-images]`, cycles the card's
 *   primary image src through the list of URLs in data-hover-images (`||` separated).
 *   On mouseleave, snaps back to the original src.
 *
 * Why:
 *   Dawn's stock behaviour is a single-swap to `media[1]` on hover. We want the card
 *   to cycle through ALL images that are linked to this card's variant (or to the
 *   product's common media when the card is a single-variant card).
 *
 * Pairs with the Liquid logic in `snippets/card-product.liquid` (0.8e block) that
 * builds the data-hover-images attribute from variant-linked or common product images.
 *
 * Performance:
 *   - Cycle interval: 800ms (feels deliberate, not frenetic)
 *   - Uses WeakMap to avoid memory leaks on dynamic content
 *   - MutationObserver re-inits for ajax-loaded cards (quick add modal, infinite scroll)
 *   - First src swap may flicker as CDN fetches; not preloading aggressively to keep
 *     payload light. Most BG products have ≤5 hover images so the impact is minor.
 *
 * Browser support: Modern evergreen (no IE). WeakMap + MutationObserver + arrow fns required.
 */

(function () {
  'use strict';

  var HOVER_INTERVAL_MS = 800;
  var ATTR = 'data-hover-images';
  var SEPARATOR = '||';
  var IMG_SELECTOR = '.card__media img, .media img, img.motion-reduce';

  // Per-card state without polluting DOM dataset further
  var cardState = new WeakMap();

  function findPrimaryImage(card) {
    // Try the most-specific first; Dawn variations may differ slightly across themes
    return card.querySelector(IMG_SELECTOR);
  }

  function initCard(card) {
    if (cardState.has(card)) return;

    var raw = card.getAttribute(ATTR) || '';
    var images = raw.split(SEPARATOR).map(function (s) { return s.trim(); }).filter(Boolean);
    // Need ≥ 2 images to cycle (otherwise it's just the static primary)
    if (images.length < 2) return;

    var imgEl = findPrimaryImage(card);
    if (!imgEl) return;

    var state = {
      images: images,
      imgEl: imgEl,
      originalSrc: imgEl.getAttribute('src'),
      originalSrcset: imgEl.getAttribute('srcset'),
      intervalId: null,
      currentIndex: 0
    };
    cardState.set(card, state);

    card.addEventListener('mouseenter', function () { startCycle(card); });
    card.addEventListener('mouseleave', function () { stopCycle(card); });
    // Touch devices: a single tap should NOT trigger cycling — leave default link behaviour
    // (no special handling needed; mouseenter doesn't fire reliably on touch)
  }

  function startCycle(card) {
    var s = cardState.get(card);
    if (!s || s.intervalId) return;
    s.currentIndex = 0;
    s.intervalId = setInterval(function () {
      s.currentIndex = (s.currentIndex + 1) % s.images.length;
      // Swap src; also clear srcset so browser uses the new src (not the cached responsive set)
      s.imgEl.removeAttribute('srcset');
      s.imgEl.src = s.images[s.currentIndex];
    }, HOVER_INTERVAL_MS);
  }

  function stopCycle(card) {
    var s = cardState.get(card);
    if (!s) return;
    if (s.intervalId) {
      clearInterval(s.intervalId);
      s.intervalId = null;
    }
    if (s.imgEl) {
      if (s.originalSrc) s.imgEl.src = s.originalSrc;
      if (s.originalSrcset) {
        s.imgEl.setAttribute('srcset', s.originalSrcset);
      }
    }
  }

  function initAll(root) {
    root = root || document;
    var cards = root.querySelectorAll('.card-wrapper[' + ATTR + ']');
    for (var i = 0; i < cards.length; i++) {
      initCard(cards[i]);
    }
  }

  // Initial pass after DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Re-init for dynamically loaded cards (quick-add modal, ajax pagination, etc.)
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('.card-wrapper[' + ATTR + ']')) {
          initCard(node);
        } else if (node.querySelectorAll) {
          initAll(node);
        }
      }
    }
  });
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }
})();
